/**
 * The shape of the inventory as the API serves it. One definition, shared by
 * the web app and — when it arrives — React Native, so every surface reads the
 * same record (spec §5).
 */

export const EQUIPMENT_CATEGORIES = [
  'Lighting Fixture',
  'Electronic Equipment',
  'Audio Equipment',
  'Cabling',
] as const

export type EquipmentCategory = (typeof EQUIPMENT_CATEGORIES)[number]

export type UserRole = 'Admin' | 'Crew'

/** `n/a` for cabling, which carries no PAT record at all. */
export type PatStatus = 'ok' | 'overdue' | 'missing' | 'n/a'

export type StocktakeStatus = 'in_progress' | 'completed' | 'abandoned'

/**
 * Whether a piece of kit works (issue #59). This was `boolean | null`, where
 * `null` quietly meant "this category does not ask" — `Not applicable` is that
 * state given a name, so nothing is lost and no reader has to know that a blank
 * meant cabling.
 *
 * `Not tested` is the one genuinely new state, and it is what a new electrical
 * item is: the dashboard flags it, and never flags `Not applicable`.
 */
export const WORKING_STATES = [
  'Working',
  'Not working',
  'Not tested',
  'Not applicable',
] as const

export type WorkingState = (typeof WORKING_STATES)[number]

/** The three a form ever offers — `Not applicable` follows from the category. */
export const SELECTABLE_WORKING_STATES: WorkingState[] = [
  'Working',
  'Not working',
  'Not tested',
]

export interface LocationRef {
  id: number
  name: string
}

export interface FixtureModelRef {
  id: number
  name: string
  /** `{brand} {name}`, assembled by the API (issue #59). */
  displayName: string
}

/**
 * A photograph of kit (issue #56).
 *
 * `url` is a stable relative path on the API, never a presigned S3 URL: one
 * that expired while a page was open would be a broken image somebody has to
 * reload to understand. It is behind the sign-in like everything else, so it
 * needs the bearer token and cannot simply be an `<img src>` — see
 * `useImageObjectUrl` in the web app.
 *
 * `source` is which record the picture came from. A lantern with none of its
 * own shows its fixture model's catalogue shot, and a screen has to be able to
 * caption that as a picture of the *kind* rather than of the unit in front of
 * somebody.
 */
export interface RecordImage {
  url: string
  source: 'item' | 'model'
  /**
   * Set while the photograph is only on this device (issue #64, step 4). The
   * `url` is then a `queued:` reference to the blob rather than an API path —
   * the server has never heard of this picture, so there is nothing there to
   * fetch, and `RecordPhoto` reads it from the device instead.
   */
  pending?: boolean
}

export interface EquipmentItem {
  id: number
  category: EquipmentCategory
  /** The raw field — what sorting, searching and the edit form want. */
  name: string
  /**
   * `{brand} {name} - {mads code}`, assembled once by the API (issue #59) and
   * read by every surface that names an item. Absent parts take their
   * separators with them, so this is never `Fresnel - `.
   */
  displayName: string
  brand: string | null
  notes: string | null
  quantity: number
  location: LocationRef | null
  madsCode: string | null
  patTestCode: string | null
  patTestDate: string | null
  patStatus: PatStatus
  workingState: WorkingState
  /**
   * The make and model this lantern is one of (issue #30). While it is set the
   * optical fields below are the model's, not the item's own — read them the
   * same way either way.
   */
  fixtureModel: FixtureModelRef | null
  lightType: string | null
  lamp: string | null
  watts: number | null
  /**
   * Whether the lamp and the watts above came from the item rather than its
   * model (issue #59). The values are resolved either way; these are what let
   * a form show an inherited value as inherited rather than as something this
   * lantern was typed with.
   */
  lampOverridden: boolean
  wattsOverridden: boolean
  gelFrame: string | null
  goboFrame: string | null
  barnDoor: string | null
  /** Human-readable reasons this item shows on the dashboard. */
  attention: string[]
  isElectrical: boolean
  isLighting: boolean
  /**
   * The photograph this item shows: its own if it has one, its fixture model's
   * otherwise (issue #56).
   *
   * **Absent** — not null — on an installation with no bucket configured. Null
   * means this item has no photograph; absent means photographs are not stored
   * here at all, which `Session.imagesConfigured` states once rather than
   * leaving every record to imply.
   */
  image?: RecordImage | null
  updatedAt: string
  /**
   * When this item was moved to the trash, or null while it is live — which is
   * everything the inventory endpoints serve. Only the trash listing sets it.
   */
  deletedAt: string | null
  /** Who binned it; null when that account has since been removed. */
  deletedBy: string | null
  /**
   * Client-only, and set only while a record made offline is waiting to sync
   * (issue #64). The server never sends either: `pending` is what makes a row
   * read as "waiting" rather than as saved, and `clientId` is the name the
   * queue knows it by until an id comes back. Both go the moment sync answers
   * and the API's own record replaces this one.
   */
  pending?: boolean
  clientId?: string
}

/** An item on the dashboard's "needs attention" table. */
export interface AttentionItem extends EquipmentItem {
  issue: string
}

export interface Location {
  id: number
  name: string
  description: string | null
  position: number
  /** Live kit only — a binned item is not counted against its old shelf. */
  itemCount: number
  unitCount: number
  /**
   * When this location was binned, or null while it is live — which is
   * everything `/api/locations` serves. Only the trash listing sets it.
   */
  deletedAt: string | null
  /** Who binned it; null when that account has since been removed. */
  deletedBy: string | null
  /** See `EquipmentItem.pending` — a place made offline, not yet synced. */
  pending?: boolean
  clientId?: string
}

/** A place to buy a spare, or the manual — hung off a fixture model. */
export interface FixtureLink {
  id: number
  label: string | null
  url: string
}

/**
 * A make and model of lantern (issue #30). The optics, notes and spares links
 * are described here once, and every item of that kind links to it rather than
 * repeating them.
 */
export interface FixtureModel {
  id: number
  name: string
  /** `{brand} {name}`, assembled by the API (issue #59). */
  displayName: string
  brand: string | null
  lightType: string | null
  lamp: string | null
  watts: number | null
  gelFrame: string | null
  goboFrame: string | null
  barnDoor: string | null
  notes: string | null
  links: FixtureLink[]
  itemCount: number
  unitCount: number
  /**
   * The catalogue shot for this kind of lantern (issue #56) — always
   * `source: 'model'`, since this is the model. Absent where photographs are
   * not stored, exactly as on an item.
   */
  image?: RecordImage | null
  updatedAt: string
  /** See `EquipmentItem.pending` — a model made offline, not yet synced. */
  pending?: boolean
  clientId?: string
}

export interface User {
  id: number
  name: string
  email: string
  /**
   * Null when the account has no number — which is an ordinary thing to be
   * (issue #58) rather than only a record predating the field.
   */
  phone: string | null
  role: UserRole
  active: boolean
  createdAt: string
  /**
   * What actually stands between this account and a password alone
   * (issue #58), worked out by the API from the same facts the sign-in reads
   * rather than from a flag: a registered passkey wins outright, otherwise a
   * texted code where the installation and the account can both use one.
   */
  secondFactor: SecondFactor
}

/**
 * `none` is the one worth noticing — but only on an installation that could do
 * better, which is why the user list is sent `smsConfigured` beside it
 * (issue #58). With Twilio switched off every account reads `none`, and that is
 * the deployment's doing rather than anybody's fault.
 */
export type SecondFactor = 'passkey' | 'sms' | 'none'

/** The admin user list, and whether this installation can text at all. */
export interface UserList {
  users: User[]
  smsConfigured: boolean
}

export interface Permissions {
  manageUsers: boolean
  manageLocations: boolean
}

export interface Session {
  user: User
  permissions: Permissions
  /**
   * Whether this installation stores photographs of kit at all (issue #56) —
   * one fact about the deployment, sent once, the way `UserList.smsConfigured`
   * is. False leaves the upload control off the item and fixture-model pages
   * with the reason beside it, rather than offering a button that cannot work.
   */
  imagesConfigured: boolean
}

/**
 * Which social sign-in options the API actually holds credentials for
 * (issue #13). False leaves the button disabled and explained rather than
 * offering a route that cannot work.
 */
export interface AuthProviders {
  google: boolean
}

/** A passkey somebody has registered as their second factor (issue #15). */
export interface Passkey {
  id: number
  /** What its owner calls it, since nothing else tells two keys apart. */
  label: string
  createdAt: string
  /** Null until it has actually signed somebody in. */
  lastUsedAt: string | null
}

/**
 * A WebAuthn ceremony the API has started and is waiting on.
 *
 * `publicKey` is passed to `navigator.credentials` untouched apart from the
 * base64url fields the browser wants as `ArrayBuffer`s — it is the API that
 * decides the challenge, the timeout and which credentials are acceptable, and
 * a client that edited any of it would only be failing the check at the far
 * end.
 */
export interface PasskeyChallenge {
  /** Single-use, and what the answer must be sent back with. */
  challengeToken: string
  publicKey: Record<string, unknown>
}

/**
 * A sign-in waiting on a code texted through Twilio Verify (issue #16).
 *
 * `phoneHint` is the last three digits of the number the message went to, with
 * the rest struck out — enough for the account holder to know which phone to
 * pick up, and not enough for anyone reading the screen to learn the number.
 * The code itself is nowhere near this: Twilio holds it, and the API only ever
 * asks Twilio whether what was typed matches.
 */
export interface SmsChallenge {
  /** Single-use, and what the code must be sent back with. */
  challengeToken: string
  phoneHint: string
  /**
   * How long ticking "Trust this device" lasts. Sent by the API rather than
   * known here, so the sentence somebody is agreeing to cannot drift from the
   * cookie it describes.
   */
  trustDeviceDays: number
}

/**
 * What a correct email and password gets you: a session, or the second step —
 * a passkey where the account has one (issue #15), otherwise a texted code
 * where the installation and the account can both use one (issue #16).
 *
 * A discriminated union rather than an optional token, so a caller cannot read
 * the response as a signed-in session by forgetting to check.
 */
export type SignInResult =
  | { mfa: null; token: string }
  | ({ mfa: 'passkey' } & PasskeyChallenge)
  | ({ mfa: 'sms' } & SmsChallenge)

/**
 * What second factor an account actually has (issue #16), for the profile
 * screen to describe. Nothing here is a setting: SMS follows from a mobile
 * number on an installation with Twilio configured, so this is worked out from
 * the same facts the sign-in reads rather than from a flag that could disagree
 * with them.
 */
export interface TwoFactorStatus {
  /** False on an installation with no Twilio credentials — nobody is texted. */
  smsConfigured: boolean
  /** False as well when this account has no mobile number to text. */
  smsAvailable: boolean
  /** The masked number, or null when there is no mobile number on the record. */
  phoneHint: string | null
  /** How many browsers are currently allowed to skip the texted code. */
  trustedDevices: number
  /** How long trusting one lasts, so the screen says it rather than guessing. */
  trustedDeviceDays: number
}

export interface DashboardStats {
  totalItems: number
  totalUnits: number
  totalLocations: number
  unassignedItems: number
  patDue: number
  notWorking: number
  /** Kit nobody has checked yet (issue #59) — flagged, but not a fault. */
  notTested: number
  needsAttention: number
}

export interface StocktakeSummary {
  id: number
  /** Set while this walk is still only on the device (issue #64, step 3). */
  pending?: boolean
  clientId?: string
  status: StocktakeStatus
  startedAt: string
  completedAt: string | null
  startedBy: string | null
  locations: LocationRef[]
  totalLines: number
  countedLines: number
  discrepancies: number
}

export interface StocktakeLine {
  id: number
  itemId: number
  name: string
  /** How the sheet names it — `{brand} {name} - {mads code}` (issue #59). */
  displayName: string
  brand: string | null
  category: EquipmentCategory
  madsCode: string | null
  expectedQuantity: number
  countedQuantity: number | null
  counted: boolean
  discrepancy: boolean
  note: string | null
}

/** Lines grouped by location, in the order the store is walked (spec W-04). */
export interface StocktakeGroup {
  location: LocationRef | null
  lines: StocktakeLine[]
}

export interface StocktakeDetail extends StocktakeSummary {
  groups: StocktakeGroup[]
}

export interface Dashboard {
  stats: DashboardStats
  lastStocktake: StocktakeSummary | null
  openStocktake: StocktakeSummary | null
  attention: AttentionItem[]
}

export interface Autocomplete {
  categories: EquipmentCategory[]
  brands: string[]
  lightTypes: string[]
  lamps: string[]
  gelFrames: string[]
  goboFrames: string[]
  barnDoors: string[]
}

export interface InventoryFilters {
  q?: string
  category?: EquipmentCategory | 'All'
  locationId?: number | 'All'
  fixtureModelId?: number | 'All'
}

/** The writable fields of an item; the API fills in the rest. */
export interface EquipmentItemInput {
  category: EquipmentCategory
  name: string
  brand?: string | null
  notes?: string | null
  quantity?: number
  locationId?: number | null
  madsCode?: string | null
  patTestCode?: string | null
  patTestDate?: string | null
  workingState?: WorkingState
  /**
   * Linking a lantern to a model moves its optics there; `lightType`,
   * `gelFrame`, `goboFrame` and `barnDoor` are then ignored. `lamp` and
   * `watts` are not — they override the model (issue #59), and sending null
   * for either is how the lantern goes back to inheriting it.
   */
  fixtureModelId?: number | null
  lightType?: string | null
  lamp?: string | null
  watts?: number | null
  gelFrame?: string | null
  goboFrame?: string | null
  barnDoor?: string | null
}

export interface FixtureLinkInput {
  label?: string | null
  url: string
}

/**
 * The writable fields of a fixture model. `links` is the whole list in the
 * order it should read — it replaces what is stored rather than merging.
 */
export interface FixtureModelInput {
  name: string
  brand?: string | null
  lightType?: string | null
  lamp?: string | null
  watts?: number | null
  gelFrame?: string | null
  goboFrame?: string | null
  barnDoor?: string | null
  notes?: string | null
  links?: FixtureLinkInput[]
}

export interface UserInput {
  name: string
  email: string
  /**
   * Optional (issue #58). An empty string clears whatever was stored — the API
   * turns it into null — and an account without one is texted no code, so the
   * admin forms say so where the box is.
   */
  phone?: string
  role: UserRole
  active?: boolean
}

export interface PasswordChangeInput {
  /** Proof the account holder is the one at the keyboard. */
  currentPassword: string
  password: string
}

/**
 * Registering a passkey (issue #15). The current password comes with it for
 * the same reason it does when changing a password: adding a second factor is
 * a change to what signs you in, and a browser somebody walked away from must
 * not be enough to make one.
 */
export interface PasskeyInput {
  challengeToken: string
  label: string
  /** Whatever `navigator.credentials.create()` produced, in JSON form. */
  credential: Record<string, unknown>
}

export interface LocationInput {
  name: string
  description?: string | null
  position?: number
}

/**
 * Every action the API records. `AuditLog.tsx` renders a label per case and
 * shows nothing for one it does not know, so this union and that map are kept
 * in step (issue #59).
 */
export type AuditAction =
  | 'item_created'
  | 'item_updated'
  | 'item_deleted'
  | 'item_restored'
  | 'item_purged'
  | 'item_stocktake_adjusted'
  | 'location_created'
  | 'location_updated'
  | 'location_deleted'
  | 'location_restored'
  | 'location_purged'
  | 'locations_reordered'
  | 'fixture_model_created'
  | 'fixture_model_updated'
  | 'fixture_model_deleted'
  | 'user_created'
  | 'user_updated'
  | 'user_deactivated'
  | 'user_deleted'
  | 'user_signed_in'
  | 'user_password_accepted'
  | 'user_sign_in_failed'
  | 'user_sign_in_throttled'
  | 'user_sign_in_refused'
  | 'user_second_factor_failed'
  | 'user_password_changed'
  | 'user_password_reset'
  | 'user_passkey_added'
  | 'user_passkey_removed'
  | 'user_devices_forgotten'
  | 'stocktake_started'
  | 'stocktake_completed'
  | 'stocktake_abandoned'
  /** One reconnection's worth of catching up (issue #64). */
  | 'changes_synced'

export interface AuditChange {
  field: string
  label: string
  from: string | null
  to: string | null
}

/**
 * How somebody signed in, or tried to (issue #58). Kept off the action name so
 * that one event does not become five.
 */
export type SignInMethod = 'password' | 'passkey' | 'sms' | 'trusted-device' | 'google'

/**
 * What a sign-in entry has to say that a field diff cannot (issue #58): the
 * method, and where from. Never what was typed — not the password, not the
 * code, and not the email address of a failed attempt.
 */
export interface AuditContext {
  method?: SignInMethod
  ip?: string
  userAgent?: string
  /**
   * A sync's tally (issue #64) — how many queued changes one reconnection
   * landed, disputed and had refused. Here for the same reason the sign-in
   * method is: it is not a before and after, and forcing it into `changes`
   * would be writing prose into a diff.
   */
  applied?: number
  conflicted?: number
  rejected?: number
}

/** One entry in a change history — an item's, or an account's (issue #58). */
export interface AuditLogEntry {
  id: number
  action: AuditAction
  actorName: string
  /** Set on an edit; null on everything that is not a before and after. */
  changes: AuditChange[] | null
  /** Set on the sign-in and account-security entries; null on the rest. */
  context: AuditContext | null
  createdAt: string
  /**
   * When the change was actually made, where that is not when it was written
   * (issue #64) — and, by being set at all, that it arrived from a device that
   * had been offline. Null on everything made at a desk, which is almost
   * everything.
   */
  occurredAt: string | null
}
